/**
 * ContinuationResolver — 核心续渲路由。
 *
 * 提供 useDocRendererContinuation() hook，返回 ContinuationContextValue，
 * 可直接传给 ContinuationProvider。
 *
 * 内部按 target.kind 路由到对应的渲染组件，并用 ContinuationGuard 保护。
 *
 * Architecture v2: 使用 DocContext + PluginBlock / ForBlock / ContentBlock。
 */

import type {
    ContinuationContextValue,
    ContinuationTarget,
} from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import { useCallback, useMemo } from "react";
import { PluginBlock } from "../document-renderer/blocks/PluginBlock";
import { resolveAndRender } from "../document-renderer/blocks/resolveAndRender";
import { useDocContext } from "../document-renderer/contexts/DocContext";
import { useExecutionContext } from "../document-renderer/contexts/ExecutionContext";
import { getThemeAccentStyle } from "../../theme/runtime/theme-accents";
import { ContinuationGuard } from "./ContinuationGuard";
import { FragmentRenderer } from "./FragmentRenderer";

type ContinuationRenderMode = "inline" | "block";

/* ── Shared error placeholder ── */

function MissingTarget({ kind, id }: { kind: string; id: string }) {
    const t = useT("editor");
    return (
        <div
            className="rounded-lg border border-dashed p-3 text-center theme-accent-banner"
            style={getThemeAccentStyle("warning")}
        >
            <div className="text-[11px] font-medium">
                {t("continuation.missingTarget", { kind, id })}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
                {t("continuation.checkReference")}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════════
 * DocumentRenderer Pipeline (store-free, DocContext)
 * ══════════════════════════════════════════════════════════ */

/**
 * 为 DocumentRenderer 管线创建 ContinuationContextValue。
 * 使用 DocContext + ExecutionContext 获取数据。
 */
export function useDocRendererContinuation(
    defaultMode: ContinuationRenderMode = "block",
): ContinuationContextValue {
    const docCtx = useDocContext();
    const execCtx = useExecutionContext();

    const resolve = useCallback(
        (target: ContinuationTarget): React.ReactNode => {
            const mode = target.mode ?? defaultMode;
            const attrs = target.attrs ?? {};

            return (
                <ContinuationGuard target={target}>
                    <DocRendererContinuationInner
                        target={target}
                        mode={mode}
                        attrs={attrs}
                        docCtx={docCtx}
                        execCtx={execCtx}
                    />
                </ContinuationGuard>
            );
        },
        [defaultMode, docCtx, execCtx],
    );

    return useMemo(
        () => ({ resolve, defaultMode, depth: 0 }),
        [defaultMode, resolve],
    );
}

function DocRendererContinuationInner({
    target,
    mode,
    attrs,
    docCtx,
    execCtx,
}: {
    target: ContinuationTarget;
    mode: "inline" | "block";
    attrs: Record<string, string>;
    docCtx: ReturnType<typeof useDocContext>;
    execCtx: ReturnType<typeof useExecutionContext>;
}) {
    switch (target.kind) {
        case "definition":
            // PluginBlock 自带 definition→for→content 三路分派 + 「未找到」占位符，
            // 历史上此分支总是直接交给它处理（即使 id 实际不在 definitions 中）。
            // 保留原行为：不经 resolveAndRender 的 kind="definition" 短路。
            return <PluginBlock definitionId={target.id} attrs={attrs} mode={mode} />;

        case "content": {
            const contentExec = execCtx?.contentExecutions?.get(target.id);
            const node = resolveAndRender(
                {
                    targetId: target.id,
                    attrs,
                    mode,
                    kind: "content",
                    contentExecution: contentExec,
                    contentOnRun: docCtx.onRunContent,
                },
                docCtx,
            );
            return node ?? <MissingTarget kind="content" id={target.id} />;
        }

        case "for": {
            const node = resolveAndRender(
                { targetId: target.id, attrs, mode, kind: "for" },
                docCtx,
            );
            return node ?? <MissingTarget kind="for" id={target.id} />;
        }

        case "import-document": {
            const node = resolveAndRender(
                { targetId: target.id, attrs, mode, kind: "import-document" },
                docCtx,
            );
            return node ?? <MissingTarget kind="import document" id={target.id} />;
        }

        case "fragment":
            return (
                <FragmentRenderer
                    source={target.source}
                    policy={target.policy ?? "safe"}
                    mode={mode}
                />
            );
    }
}
